import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import DashboardLayout from "../components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { useAuth } from "../context/AuthContext";
import api, { formatCurrency, formatDate } from "../lib/api";
import { toast } from "sonner";
import { 
  CreditCard, 
  CheckCircle2,
  Clock,
  DollarSign,
  Loader2,
  AlertCircle
} from "lucide-react";

const PaymentsPage = () => {
  const { user, isAdmin, refreshUser } = useAuth();
  const [payments, setPayments] = useState([]);
  const [allPayments, setAllPayments] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const fetchData = useCallback(async () => {
    try {
      const [paymentsRes, settingsRes] = await Promise.all([
        api.get(`/payments/user/${user?.id}`),
        isAdmin ? api.get("/settings") : Promise.resolve({ data: null })
      ]);
      setPayments(paymentsRes.data);
      setSettings(settingsRes.data);

      if (isAdmin) {
        const allRes = await api.get("/payments/all");
        setAllPayments(allRes.data);
      }
    } catch (error) {
      toast.error("Failed to load payment data");
    } finally {
      setLoading(false);
    }
  }, [user?.id, isAdmin]);

  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error("Payment status check timed out. Please check your email for confirmation.");
      setCheckingStatus(false);
      return;
    }

    setCheckingStatus(true);

    try {
      const response = await api.get(`/payments/stripe/status/${sessionId}`);
      const data = response.data;

      if (data.payment_status === "paid") {
        toast.success(`Payment successful! Your ${data.payment_type === "registration" ? "registration" : "membership"} has been activated.`);
        setCheckingStatus(false);
        refreshUser();
        fetchData();
        return;
      } else if (data.status === "expired") {
        toast.error("Payment session expired. Please try again.");
        setCheckingStatus(false);
        return;
      }

      // Continue polling if payment is still pending
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error("Error checking payment status:", error);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      } else {
        toast.error("Error checking payment status. Please refresh the page.");
        setCheckingStatus(false);
      }
    }
  }, [refreshUser, fetchData]);

  // Check for return from Stripe
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    const status = searchParams.get("status");

    if (sessionId && status === "success") {
      // Start polling for payment status
      pollPaymentStatus(sessionId);
      // Clear the URL params
      setSearchParams({});
    } else if (status === "cancelled") {
      toast.error("Payment was cancelled");
      setSearchParams({});
    }
  }, [searchParams, pollPaymentStatus, setSearchParams]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const initiateStripePayment = async (type) => {
    setProcessing(true);
    try {
      const originUrl = window.location.origin;
      const response = await api.post("/payments/stripe/checkout", {
        payment_type: type,
        origin_url: originUrl
      });

      // Redirect to Stripe Checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error("No checkout URL received");
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to initiate payment";
      toast.error(errorMsg);
    } finally {
      setProcessing(false);
    }
  };

  const completePayment = async (paymentId) => {
    try {
      await api.post(`/payments/${paymentId}/complete?transaction_id=TXN_${Date.now()}`);
      toast.success("Payment marked as completed");
      fetchData();
      refreshUser();
    } catch (error) {
      toast.error("Failed to complete payment");
    }
  };

  const getPaymentStatusBadge = (payment) => {
    const status = payment.payment_status || payment.status;
    if (status === "paid" || status === "completed") {
      return <Badge className="bg-emerald/20 text-emerald">Completed</Badge>;
    } else if (status === "pending" || status === "initiated") {
      return <Badge className="bg-orange-500/20 text-orange-400">Pending</Badge>;
    } else if (status === "expired") {
      return <Badge className="bg-red-500/20 text-red-400">Expired</Badge>;
    }
    return <Badge className="bg-gray-500/20 text-gray-400">{status}</Badge>;
  };

  if (checkingStatus) {
    return (
      <DashboardLayout title="Payments" subtitle="Processing your payment...">
        <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
          <Loader2 className="w-12 h-12 text-burgundy animate-spin" />
          <p className="text-white text-lg">Verifying your payment...</p>
          <p className="text-gray-400">Please wait while we confirm your transaction.</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout 
      title="Payments" 
      subtitle="Manage your registration and membership fees"
    >
      {/* Payment Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {/* Registration Fee */}
        <Card className={`border-white/5 ${user?.registration_paid ? "bg-emerald/10" : "bg-surface"}`}>
          <CardHeader>
            <CardTitle className="text-white font-serif flex items-center gap-2">
              <DollarSign className="text-burgundy" size={20} />
              Registration Fee
            </CardTitle>
            <CardDescription>One-time fee to join the EchoSense Capital Club</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="text-3xl font-bold text-platinum tabular-nums">
                  {formatCurrency(settings?.registration_fee || 999)}
                </p>
                <p className="text-gray-500 text-sm">One-time payment</p>
              </div>
              {user?.registration_paid ? (
                <CheckCircle2 className="text-emerald" size={32} />
              ) : (
                <Clock className="text-orange-400" size={32} />
              )}
            </div>
            
            {!user?.registration_paid && (
              <Button 
                onClick={() => initiateStripePayment("registration")}
                disabled={processing}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
                data-testid="pay-registration-btn"
              >
                {processing ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    Redirecting...
                  </>
                ) : (
                  <>
                    <CreditCard size={16} className="mr-2" />
                    Pay with Stripe
                  </>
                )}
              </Button>
            )}
            
            {user?.registration_paid && (
              <Badge className="bg-emerald/20 text-emerald w-full justify-center py-2">
                <CheckCircle2 size={14} className="mr-2" />
                Paid
              </Badge>
            )}
          </CardContent>
        </Card>

        {/* Monthly Membership */}
        <Card className={`border-white/5 ${user?.membership_active ? "bg-emerald/10" : "bg-surface"}`}>
          <CardHeader>
            <CardTitle className="text-white font-serif flex items-center gap-2">
              <CreditCard className="text-burgundy" size={20} />
              Monthly Membership
            </CardTitle>
            <CardDescription>Recurring monthly fee</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="text-3xl font-bold text-platinum tabular-nums">
                  {formatCurrency(settings?.monthly_fee || 99)}
                </p>
                <p className="text-gray-500 text-sm">Per month</p>
              </div>
              {user?.membership_active ? (
                <CheckCircle2 className="text-emerald" size={32} />
              ) : (
                <Clock className="text-orange-400" size={32} />
              )}
            </div>
            
            {!user?.membership_active && user?.registration_paid && (
              <Button 
                onClick={() => initiateStripePayment("monthly")}
                disabled={processing}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
                data-testid="pay-monthly-btn"
              >
                {processing ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    Redirecting...
                  </>
                ) : (
                  <>
                    <CreditCard size={16} className="mr-2" />
                    Pay with Stripe
                  </>
                )}
              </Button>
            )}
            
            {user?.membership_active && (
              <Badge className="bg-emerald/20 text-emerald w-full justify-center py-2">
                <CheckCircle2 size={14} className="mr-2" />
                Active
              </Badge>
            )}
            
            {!user?.registration_paid && !user?.membership_active && (
              <div className="flex items-center gap-2 text-orange-400 text-sm justify-center">
                <AlertCircle size={14} />
                <span>Complete registration first</span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Stripe Info */}
      <Card className="bg-surface/50 border-burgundy/20 mb-8">
        <CardContent className="py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#635BFF] rounded flex items-center justify-center">
              <span className="text-white font-bold text-sm">S</span>
            </div>
            <div>
              <p className="text-white font-medium">Secure Payments via Stripe</p>
              <p className="text-gray-400 text-sm">Your payment information is processed securely. We never store your card details.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Payment History */}
      <Card className="bg-surface border-white/5">
        <CardHeader>
          <CardTitle className="text-white font-serif">Payment History</CardTitle>
          <CardDescription>Your payment transactions</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="payments-table">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Date</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Type</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-400">Amount</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Status</th>
                  {isAdmin && <th className="text-right py-3 px-4 text-sm font-medium text-gray-400">Action</th>}
                </tr>
              </thead>
              <tbody>
                {(isAdmin ? allPayments : payments).map((payment) => (
                  <tr key={payment.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                    <td className="py-3 px-4 text-gray-400">{formatDate(payment.created_at)}</td>
                    <td className="py-3 px-4">
                      <Badge className={payment.payment_type === "registration" ? "bg-burgundy/20 text-burgundy" : "bg-blue-500/20 text-blue-400"}>
                        {payment.payment_type}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-right text-slate-900 font-medium tabular-nums">
                      {formatCurrency(payment.amount)}
                    </td>
                    <td className="py-3 px-4">
                      {getPaymentStatusBadge(payment)}
                    </td>
                    {isAdmin && (
                      <td className="py-3 px-4 text-right">
                        {(payment.status === "pending" && !payment.stripe_session_id) && (
                          <Button 
                            size="sm" 
                            onClick={() => completePayment(payment.id)}
                            className="bg-emerald hover:bg-emerald-dark text-white"
                          >
                            Mark Paid
                          </Button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
                {(isAdmin ? allPayments : payments).length === 0 && (
                  <tr>
                    <td colSpan={isAdmin ? 5 : 4} className="py-12 text-center text-gray-500">
                      No payment records yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </DashboardLayout>
  );
};

export default PaymentsPage;
